import React, { useState, useRef, useEffect } from 'react';
import { useDrag } from 'react-dnd';

const PuzzlePiece = ({ piece, onRemove, onScale, onTouchMove, onTouchDrop, onUpdateDimensions, isPreview = false, fromWorkspace = false }) => {
  console.log('PuzzlePiece rendering:', { piece, isPreview, fromWorkspace });

  const [isScaling, setIsScaling] = useState(false);
  const [scaleStartPos, setScaleStartPos] = useState(null);
  const [scaleStartSize, setScaleStartSize] = useState(null);
  const [isDraggingTouch, setIsDraggingTouch] = useState(false);
  const [imageNaturalDimensions, setImageNaturalDimensions] = useState(null);
  const [dimensionsUpdated, setDimensionsUpdated] = useState(false);
  const pieceRef = useRef(null);
  const listenersRef = useRef(new Set()); // Track active listeners

  const [{ isDragging }, drag] = useDrag({
    type: 'area',
    item: { ...piece, fromWorkspace },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    }),
    canDrag: !isScaling // Disable dragging while scaling
  });

  // Cleanup function to remove all listeners
  const cleanupAllListeners = () => {
    listenersRef.current.forEach(cleanup => {
      try {
        cleanup();
      } catch (e) {
        console.log('Error during listener cleanup:', e);
      }
    });
    listenersRef.current.clear();
    console.log('🧹 All touch listeners cleaned up');
  };

  // Clean up on unmount
  useEffect(() => {
    return cleanupAllListeners;
  }, []);

  const currentScale = piece.scale || 1;

  // Always use the piece's stored dimensions, which have been corrected on image load
  const scaledWidth = (piece.width || 120) * currentScale;
  const scaledHeight = (piece.height || 80) * currentScale;

  // Debug logging for aspect ratio issues
  if (!isPreview && fromWorkspace) {
    const calculatedAspectRatio = scaledWidth / scaledHeight;
    const storedAspectRatio = (piece.width || 120) / (piece.height || 80);
    console.log('PuzzlePiece dimensions:', {
      pieceId: piece.id,
      stored: { width: piece.width, height: piece.height, aspectRatio: storedAspectRatio },
      scale: currentScale,
      calculated: { width: scaledWidth, height: scaledHeight, aspectRatio: calculatedAspectRatio },
      imageNaturalDimensions
    });
  }

  const handleScaleStart = (e) => {
    if (isPreview || !onScale) return;

    e.preventDefault();
    e.stopPropagation();

    // Handle both mouse and touch events
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);

    if (clientX === undefined || clientY === undefined) return;

    console.log('Scale start:', { pieceId: piece.id, currentScale, eventType: e.type });

    const startX = clientX;
    const startY = clientY;
    const startScale = currentScale;

    setIsScaling(true);

    const handleScaleMove = (moveEvent) => {
      // Handle both mouse and touch events for move
      const moveClientX = moveEvent.clientX || (moveEvent.touches && moveEvent.touches[0].clientX);
      const moveClientY = moveEvent.clientY || (moveEvent.touches && moveEvent.touches[0].clientY);

      if (moveClientX === undefined || moveClientY === undefined) return;

      const deltaX = moveClientX - startX;
      const deltaY = moveClientY - startY;
      const delta = Math.max(deltaX, deltaY); // Use the larger delta for proportional scaling

      const scaleFactor = 1 + (delta / 150); // Adjust sensitivity (was 100, now 150 for smoother scaling)
      const newScale = Math.max(0.1, Math.min(5, startScale * scaleFactor)); // Limit scale between 0.1x and 5x

      console.log('Scaling:', { delta, scaleFactor, newScale, eventType: moveEvent.type });
      onScale(piece.id, newScale);
    };

    const handleScaleEnd = () => {
      console.log('Scale end');
      setIsScaling(false);
      document.removeEventListener('mousemove', handleScaleMove);
      document.removeEventListener('mouseup', handleScaleEnd);
      document.removeEventListener('touchmove', handleScaleMove);
      document.removeEventListener('touchend', handleScaleEnd);
    };

    document.addEventListener('mousemove', handleScaleMove);
    document.addEventListener('mouseup', handleScaleEnd);
    document.addEventListener('touchmove', handleScaleMove);
    document.addEventListener('touchend', handleScaleEnd);
  };

  // Touch drag handlers for mobile support - Scroll-friendly implementation
  const handleTouchStart = (e) => {
    console.log('🎯 MAIN TOUCH START:', {
      pieceId: piece.id,
      isPreview,
      fromWorkspace,
      isScaling
    });

    if (isScaling) {
      console.log('⏸️ Skipping touch - currently scaling');
      return;
    }

    // Handle preview pieces differently (for drag to workspace)
    if (isPreview) {
      console.log('👆 Handling preview piece touch');
      handlePreviewTouchStart(e);
      return;
    }

    // Handle workspace pieces (for repositioning) - only if fromWorkspace
    if (fromWorkspace) {
      console.log('🎪 Handling workspace piece touch');
      handleWorkspaceTouchStart(e);
    } else {
      console.log('❌ Piece not fromWorkspace, ignoring touch');
    }
  };

  // Workspace piece repositioning with scroll preservation - Debug version
  const handleWorkspaceTouchStart = (e) => {
    console.log('🚀 ENTERING handleWorkspaceTouchStart function');
    console.log('📱 Event object:', e);
    console.log('👆 Touches array:', e.touches);
    console.log('🎯 Touches length:', e.touches ? e.touches.length : 'NO TOUCHES');

    if (!e.touches || e.touches.length === 0) {
      console.log('❌ NO TOUCHES FOUND IN EVENT');
      return;
    }

    const touch = e.touches[0];
    console.log('👆 First touch:', touch);

    if (!touch) {
      console.log('❌ FIRST TOUCH IS NULL/UNDEFINED');
      return;
    }

    const startX = touch.clientX;
    const startY = touch.clientY;
    const startTime = Date.now();
    let isRepositioning = false;
    let hasMovedSignificantly = false;

    console.log('🔥 WORKSPACE PIECE TOUCH START:', { pieceId: piece.id, startX, startY });

    // Immediate visual feedback to show touch is registered
    console.log('🎨 Applying visual feedback...');
    if (pieceRef.current) {
      console.log('✅ pieceRef.current exists, applying styles...');
      pieceRef.current.style.backgroundColor = 'rgba(255, 0, 0, 0.1)'; // Red tint to show touch
      pieceRef.current.style.border = '3px solid red';
      console.log('✅ Applied immediate touch feedback');
    } else {
      console.log('❌ pieceRef.current is null/undefined');
    }

    const startReposition = () => {
      if (isRepositioning) return;
      isRepositioning = true;
      setIsDraggingTouch(true);

      // Add visual feedback - slightly scale the piece to show it's selected
      if (pieceRef.current) {
        pieceRef.current.style.transform = 'scale(1.05)';
        pieceRef.current.style.zIndex = '1000';
        pieceRef.current.style.boxShadow = '0 8px 16px rgba(0,0,0,0.3)';
      }

      console.log('Starting workspace piece repositioning:', { pieceId: piece.id });
    };

    const handleTouchMove = (moveEvent) => {
      const moveTouch = moveEvent.touches[0];
      const deltaX = Math.abs(moveTouch.clientX - startX);
      const deltaY = Math.abs(moveTouch.clientY - startY);
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      const timeSinceStart = Date.now() - startTime;

      console.log('📱 TOUCH MOVE:', {
        pieceId: piece.id,
        distance: Math.round(distance),
        deltaX: Math.round(deltaX),
        deltaY: Math.round(deltaY),
        time: timeSinceStart,
        isRepositioning
      });

      hasMovedSignificantly = distance > 3; // Track any movement for touch end logic

      // ULTRA-RESPONSIVE conditions for workspace pieces:
      // Start repositioning with minimal movement (2px) OR after very short hold (100ms)
      const shouldStartReposition = distance > 2 || timeSinceStart > 100;

      if (!isRepositioning && shouldStartReposition) {
        console.log('🚀 STARTING REPOSITION - conditions met!', { distance, timeSinceStart });
        startReposition();
      }

      if (isRepositioning) {
        console.log('⚡ REPOSITIONING - preventing default and moving piece');
        moveEvent.preventDefault(); // Only prevent when actively repositioning
        const deltaXFromStart = moveTouch.clientX - startX;
        const deltaYFromStart = moveTouch.clientY - startY;

        if (onTouchMove) {
          console.log('📍 Calling onTouchMove with:', {
            newX: piece.x + deltaXFromStart,
            newY: piece.y + deltaYFromStart
          });
          onTouchMove(piece.id, piece.x + deltaXFromStart, piece.y + deltaYFromStart);
        } else {
          console.log('❌ No onTouchMove handler available');
        }
      }
    };

    const handleTouchEnd = () => {
      console.log('🏁 WORKSPACE PIECE TOUCH END:', {
        pieceId: piece.id,
        isRepositioning,
        hasMovedSignificantly,
        duration: Date.now() - startTime
      });

      setIsDraggingTouch(false);
      isRepositioning = false; // Reset the flag

      // Reset ALL visual feedback
      if (pieceRef.current) {
        pieceRef.current.style.transform = '';
        pieceRef.current.style.zIndex = '';
        pieceRef.current.style.boxShadow = '';
        pieceRef.current.style.backgroundColor = '';
        pieceRef.current.style.border = '';
        console.log('🔄 Reset visual feedback');
      }

      // CRITICAL: Remove event listeners to restore normal scrolling
      document.removeEventListener('touchmove', handleTouchMove, { passive: false });
      document.removeEventListener('touchend', handleTouchEnd);
      console.log('🧹 Cleaned up workspace touch listeners');
    };

    // Use non-passive listeners for workspace pieces to ensure responsiveness
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);

    // Track cleanup functions
    const cleanupMove = () => document.removeEventListener('touchmove', handleTouchMove, { passive: false });
    const cleanupEnd = () => document.removeEventListener('touchend', handleTouchEnd);
    listenersRef.current.add(cleanupMove);
    listenersRef.current.add(cleanupEnd);
  };

  // Handle dragging preview pieces to workspace - Scroll-friendly implementation
  const handlePreviewTouchStart = (e) => {
    const touch = e.touches[0];
    const startX = touch.clientX;
    const startY = touch.clientY;
    const startTime = Date.now();
    let isDragging = false;
    let dragStarted = false;
    let clone = null;

    console.log('👋 PREVIEW TOUCH START:', { pieceId: piece.id, startX, startY });

    // Immediate visual feedback to show touch is registered on preview
    if (pieceRef.current) {
      pieceRef.current.style.backgroundColor = 'rgba(0, 123, 255, 0.1)'; // Blue tint
      pieceRef.current.style.border = '3px solid #007bff';
      console.log('✅ Applied preview touch feedback');
    }

    const startDrag = () => {
      if (dragStarted) return;
      dragStarted = true;

      console.log('Starting preview drag:', { pieceId: piece.id });

      // Create a visual clone for dragging
      clone = pieceRef.current.cloneNode(true);
      clone.classList.add('mobile-drag-clone');
      clone.style.position = 'fixed';
      clone.style.zIndex = '9999';
      clone.style.pointerEvents = 'none';
      clone.style.opacity = '0.8';
      clone.style.left = startX + 'px';
      clone.style.top = startY + 'px';
      clone.style.transform = 'translate(-50%, -50%)';
      clone.style.border = '2px solid #007bff';
      clone.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
      document.body.appendChild(clone);
    };

    const handlePreviewTouchMove = (moveEvent) => {
      const moveTouch = moveEvent.touches[0];
      const deltaX = Math.abs(moveTouch.clientX - startX);
      const deltaY = Math.abs(moveTouch.clientY - startY);
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      const timeSinceStart = Date.now() - startTime;

      console.log('📱 PREVIEW TOUCH MOVE:', {
        pieceId: piece.id,
        distance: Math.round(distance),
        deltaX: Math.round(deltaX),
        deltaY: Math.round(deltaY),
        time: timeSinceStart,
        isDragging,
        dragStarted
      });

      // EXTREMELY lenient conditions for preview pieces - prioritize drag over scroll:
      // 1. Only 3px movement required (minimal threshold)
      // 2. Very long time window - 1500ms
      // 3. Only block pure vertical scrolls (deltaY > 50 AND minimal horizontal movement)
      const isPureVerticalScroll = deltaY > 50 && deltaX < 15; // Clear vertical scroll intent

      console.log('🧮 Movement analysis:', {
        distance,
        threshold: 3,
        timeOK: timeSinceStart < 1500,
        verticalScrollBlock: isPureVerticalScroll,
        shouldStart: !isDragging && distance > 3 && timeSinceStart < 1500 && !isPureVerticalScroll
      });

      if (!isDragging && distance > 3 && timeSinceStart < 1500 && !isPureVerticalScroll) {
        console.log('🚀 STARTING PREVIEW DRAG! (ultra-responsive)');
        isDragging = true;
        startDrag();
      }

      if (isDragging && clone) {
        console.log('⚡ DRAGGING PREVIEW CLONE');
        moveEvent.preventDefault(); // Only prevent when actively dragging
        clone.style.left = moveTouch.clientX + 'px';
        clone.style.top = moveTouch.clientY + 'px';

        // Visual feedback: check if over workspace
        const workspaceElement = document.querySelector('.workspace-canvas');
        if (workspaceElement) {
          const workspaceRect = workspaceElement.getBoundingClientRect();
          const isOverWorkspace = (
            moveTouch.clientX >= workspaceRect.left &&
            moveTouch.clientX <= workspaceRect.right &&
            moveTouch.clientY >= workspaceRect.top &&
            moveTouch.clientY <= workspaceRect.bottom
          );

          // Change clone appearance when over workspace
          if (isOverWorkspace) {
            clone.style.border = '2px solid #28a745'; // Green when over valid drop zone
            workspaceElement.classList.add('drop-over');
          } else {
            clone.style.border = '2px solid #007bff'; // Blue when not over drop zone
            workspaceElement.classList.remove('drop-over');
          }
        }
      }
    };

    const handlePreviewTouchEnd = (endEvent) => {
      const endTouch = endEvent.changedTouches[0];
      const dropX = endTouch.clientX;
      const dropY = endTouch.clientY;

      console.log('🏁 PREVIEW TOUCH END:', { dropX, dropY, isDragging, dragStarted });

      // Reset preview visual feedback
      if (pieceRef.current) {
        pieceRef.current.style.backgroundColor = '';
        pieceRef.current.style.border = '';
        console.log('🔄 Reset preview touch feedback');
      }

      // Clean up clone - use try/catch in case it was already removed
      try {
        if (clone && clone.parentNode) {
          document.body.removeChild(clone);
          console.log('🗑️ Removed drag clone');
        }
      } catch (error) {
        console.log('Clone already removed or not found');
      }

      // Only handle drop if we were actually dragging
      if (isDragging && dragStarted) {
        // Check if dropped on workspace
        const workspaceElement = document.querySelector('.workspace-canvas');
        if (workspaceElement) {
          // Remove any drop-over styling
          workspaceElement.classList.remove('drop-over');

          const workspaceRect = workspaceElement.getBoundingClientRect();
          const isOverWorkspace = (
            dropX >= workspaceRect.left &&
            dropX <= workspaceRect.right &&
            dropY >= workspaceRect.top &&
            dropY <= workspaceRect.bottom
          );

          console.log('Drop check:', {
            dropPosition: { dropX, dropY },
            workspaceRect: {
              left: workspaceRect.left,
              right: workspaceRect.right,
              top: workspaceRect.top,
              bottom: workspaceRect.bottom
            },
            isOverWorkspace
          });

          if (isOverWorkspace && onTouchDrop) {
            // Calculate relative position within workspace
            const relativeX = dropX - workspaceRect.left;
            const relativeY = dropY - workspaceRect.top;

            console.log('Dropping piece via touch:', {
              originalDrop: { dropX, dropY },
              relativePosition: { relativeX, relativeY },
              note: 'Centering and size constraining will be handled by handleTouchDrop'
            });
            // Pass the drop coordinates - handleTouchDrop will handle centering and size constraining
            onTouchDrop(piece, relativeX, relativeY);
          } else {
            console.log('Drop outside workspace or no drop handler');
          }
        } else {
          console.log('Workspace element not found');
        }
      } else {
        console.log('Touch ended without dragging - allowing normal touch behavior');
      }

      // CRITICAL: Always clean up listeners to restore scrolling
      document.removeEventListener('touchmove', handlePreviewTouchMove, { passive: false });
      document.removeEventListener('touchend', handlePreviewTouchEnd);
      console.log('🧹 Cleaned up preview touch listeners');
    };

    // Use passive listeners to preserve scroll performance
    document.addEventListener('touchmove', handlePreviewTouchMove, { passive: false });
    document.addEventListener('touchend', handlePreviewTouchEnd);

    // Track cleanup functions
    const cleanupMove = () => document.removeEventListener('touchmove', handlePreviewTouchMove, { passive: false });
    const cleanupEnd = () => document.removeEventListener('touchend', handlePreviewTouchEnd);
    listenersRef.current.add(cleanupMove);
    listenersRef.current.add(cleanupEnd);
  };

  const pieceStyle = {
    position: isPreview ? 'relative' : 'absolute',
    left: isPreview ? 'auto' : `${piece.x}px`,
    top: isPreview ? 'auto' : `${piece.y}px`,
    width: isPreview ? '140px' : `${scaledWidth}px`,
    height: isPreview ? '60px' : `${scaledHeight}px`,
    cursor: isScaling ? 'nw-resize' : 'move',
    opacity: isDragging ? 0.5 : 1,
    border: `1px solid ${isScaling ? '#ff6b35' : '#007bff'}`,
    //backgroundColor: 'white',
    overflow: 'hidden',
    userSelect: 'none'
  };

  // Clip path for future use: inset(top right bottom left)
  // const clipPath = `inset(${piece.y}px ${piece.pdfWidth - piece.x - piece.width}px ${piece.pdfHeight - piece.y - piece.height}px ${piece.x}px)`;

  return (
    <div
      ref={(el) => {
        pieceRef.current = el;
        drag(el);
      }}
      style={pieceStyle}
      className={`puzzle-piece ${isPreview ? 'preview' : ''}`}
      onTouchStart={handleTouchStart} // Re-enabled with scroll-friendly implementation
    >
      {false && onRemove && (
        <img
          src="/imgs/close.png"
          alt="Remove"
          className="remove-piece-btn"
          onClick={onRemove}
          style={{
            position: 'absolute',
            top: '0px',
            right: '0px',
            margin: '8px',
            width: '24px',
            height: '24px',
            cursor: 'pointer',
            zIndex: 100,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            borderRadius: '50%',
            backgroundColor: 'white'
          }}
          title="Remove piece"
        />
      )}

      {!isPreview && onScale && (
        <div
          className="scale-handle"
          onMouseDown={handleScaleStart}
          onTouchStart={handleScaleStart}
          style={{
            position: 'absolute',
            bottom: '-8px',
            right: '-8px',
            width: '16px',
            height: '16px',
            margin: "8px",
            backgroundColor: isScaling ? '#ff6b35' : '#007bff',
            border: '1px solid white',
            borderRadius: '3px',
            cursor: 'nw-resize',
            zIndex: 11,
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
          }}
          title="Drag to resize"
        />
      )}

      <div
        style={{
          width: "100%",
          height: "100%",
          overflow: 'hidden',
          position: 'relative',
          backgroundColor: '#f8f9fa'
        }}
      >
        {piece.extractedImage ? (
          <img
            src={piece.extractedImage}
            alt={`PDF Piece (Page ${piece.pageNumber || 1})`}
            style={{
              width: "100%",
              height: "100%",
              objectFit: 'contain',
              display: 'block'
            }}
            onLoad={(e) => {
              console.log('PuzzlePiece image loaded successfully');
              // Store the natural dimensions of the loaded image
              if (!isPreview && e.target.naturalWidth && e.target.naturalHeight && !dimensionsUpdated) {
                const naturalWidth = e.target.naturalWidth;
                const naturalHeight = e.target.naturalHeight;
                const imageAspectRatio = naturalWidth / naturalHeight;

                setImageNaturalDimensions({
                  width: naturalWidth,
                  height: naturalHeight
                });

                console.log('Image natural dimensions:', {
                  width: naturalWidth,
                  height: naturalHeight,
                  aspectRatio: imageAspectRatio
                });

                // Update the piece's stored dimensions to match the image aspect ratio
                // Keep the width the same, but adjust height based on actual image aspect ratio
                if (onUpdateDimensions && fromWorkspace) {
                  const baseWidth = piece.width || 120;
                  const correctedHeight = baseWidth / imageAspectRatio;

                  console.log('Updating piece dimensions:', {
                    originalDimensions: { width: piece.width, height: piece.height },
                    correctedDimensions: { width: baseWidth, height: correctedHeight },
                    aspectRatio: imageAspectRatio
                  });

                  onUpdateDimensions(piece.id, baseWidth, correctedHeight);
                  setDimensionsUpdated(true);
                }
              }
            }}
            onError={() => console.error('PuzzlePiece image failed to load')}
          />
        ) : (
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.8rem',
            color: '#666'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div>📄 PDF Piece</div>
              <div style={{ fontSize: '0.6rem', marginTop: '4px' }}>
                {Math.round(piece.width)}×{Math.round(piece.height)}px
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PuzzlePiece;
